import Configuration from '@/agilis/util/Configuration'
import { mapActions, mapGetters } from 'vuex'
import actionsMixin from './actionsMixin'

function parseEvent (event) {
  return {
    from: event.owner,
    to: event.droptarget,
    item: event.items[0],
    stop: event.stop
  }
}

// Mixin providing common render utils
export default {
  name: 'TrackerKanbanMixin',
  mixins: [actionsMixin],

  props: {
    fetchCardDataHook: Function
  },

  data () {
    return {
      trackerDTO: {},
      loading: false,
      actionsArray: [],
      dragging: false,
      currentColumn: null,
      jlAssertionContext: {},
      dragOptions: {
        dropzoneSelector: '.kaban__drop-zone',
        draggableSelector: '.kaban__drag-item',
        onDrop (event) { this.handleDrop(parseEvent(event)) },
        onDragstart (event) {
          // use set timeout to start our businessLogic after dragStart completed
          setTimeout(() => this.handleStart(parseEvent(event)), 0)
        },
        onDragend (event) { this.handleEnd(parseEvent(event)) }
      }
    }
  },

  computed: {
    ...mapGetters({
      'applicationId': 'contextStore/applicationId',
      'trackerTypeId': 'contextStore/trackerTypeId'
    }),

    columns () {
      let column = []
      for (const trackerState in this.trackerStates) {
        const state = this.trackerStates[trackerState]
        column.push({
          id: state.id,
          title: this.$t(state.label),
          trackers: this.items.filter(item => item.state === state.id)
        })
      }
      return column
    },

    trackerStates () {
      return Configuration.state.getStatesMap(this.applicationId, this.trackerTypeId)
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'notifyWarning': 'notificationStore/notifyWarning'
    }),

    async fetchCardData (trackerId) {
      const url = this.$axios.agilisUrlTrackerEdit(this.applicationId, this.trackerTypeId, trackerId)
      try {
        // Load tracker data
        const response = await this.$axios.get(url, this.$axios.agilisRequestConfig(url))
        this.trackerDTO = response.data

        // Call the custom hook when provided
        if (typeof this.fetchCardDataHook === 'function') {
          await this.fetchCardDataHook(this.trackerDTO)
        }

        if (this.dragging) {
          this.actionsArray = this.actionArray
        }
      } catch (error) {
        this.notifyError(error)
      }
    },

    async handleDrop ({ from, to, item }) {
      const targetState = to.dataset.id

      const actionId = this.actionsArray.find(action => action.targetState === targetState)?.id
      if (actionId) {
        to.closest('.kaban-col__content').classList.add('kaban-col__content--to')
        from.closest('.kaban-col__content').classList.remove('kaban-col__content--from')
        this.loading = true
        try {
          await this.handleActionClick(actionId)
          await this.fetchData()
          if (!this.items.find(item => item.id === this.trackerDTO.id)) {
            this.notifyWarning({
              message: this.$t('message.warningTrackerView', { trackerId: this.trackerDTO.id })
            })
          }
        } catch (error) {
          this.notifyError(error)
        }
      }
      this.resetDrop({ from, to })
      this.loading = false
    },

    handleEnd ({ from, to, item, stop }) {
      if (
        !to || // prevent drop outside a droparea
        from === to // prevent drop on same area
      ) {
        this.resetDrop({ from, to })
        stop()
      }
    },

    resetDrop ({ from, to }) {
      this.actionsArray = []
      this.dragging = false
      this.currentColumn = null

      // Reset style on from-column and to
      from && from.closest('.kaban-col__content').classList.remove('kaban-col__content--from')
      to && to.closest('.kaban-col__content').classList.remove('kaban-col__content--to')
    },

    isValidDrop (columnId) {
      return this.isActive(columnId) && !this.unsupportedAction(columnId) && !this.invalidAction(columnId)
    },

    async handleStart ({ from, item }) {
      this.loading = true

      // Add style on from-column
      from.closest('.kaban-col__content').classList.add('kaban-col__content--from')
      document.getElementsByClassName('v-data-table__wrapper')[0].scrollTop = 0

      const state = from.dataset.id
      const itemIndex = parseInt(item.dataset.index)

      this.currentColumn = state
      this.dragging = true
      const items = this.columns.find(col => col.id === state)?.trackers || []
      this.trackerDTO = items[itemIndex]

      try {
        await this.fetchCardData(this.trackerDTO.id)
      } catch (error) {
        console.log('Error fetching tracker', error)
      }
      this.loading = false
    },

    setJlAssertionContext (jlAssertionContext) {
      this.jlAssertionContext = jlAssertionContext
    },

    isActive (columnId) {
      return true
    },

    invalidAction (columnId) {
      const action = this.actionsArray.find(action => action.targetState === columnId)
      return this.dragging && action && action.invalid
    },

    unsupportedAction (columnId) {
      const action = this.actionsArray.find(action => action.targetState === columnId)
      return this.dragging && !action && this.trackerDTO.state !== columnId
    },

    invalidActionError (columnId) {
      const action = this.actionsArray.find(action => action.targetState === columnId)
      return action && action.errorMessage
    },

    draggableStyle (column) {
      return !this.isActive(column.id) ? 'height: 0; overflow: hidden;' : 'flex-grow: 1'
    },

    innerColumnCssClass (column) {
      const classes = ['kaban-col__content']
      if (this.invalidActionError(column.id)) classes.push('kaban-col__content--invalid') // style for action validation failed
      if (this.unsupportedAction(column.id)) classes.push('kaban-col__content--not-droppable') // style for no-droppable columns
      return classes.join(' ')
    },

    outerColumnStyle (column) {
      if (this.isActive(column.id)) {
        if (this.dragging) return 'width: calc(100% / ' + this.columns.length + ');' // dragging: show all cols
        return 'min-width: 20%;' // not dragging: show up to 5 cols
      }
      return 'display: none;' // not active: hide
    }
  }
}
